/**
 * @file OrderCreateSubmitCard.tsx
 * @description «Итого и отправка» формы создания заказа (phase19.24
 * шаг 5.1, §2; phase19.31 — кнопки переехали в верхнюю панель).
 *
 * Считает к оплате вместе с доставкой (её сумму даёт реестр исходов) и
 * объясняет, куда уйдёт письмо и почему отправка сейчас невозможна. Самих
 * кнопок здесь нет: главное действие — в верхней панели
 * (`OrderCreateHeader`), на телефоне — в общей нижней полосе каркаса (SC34).
 */

import { EditorialCard } from "../ui/EditorialCard";
import { NoticeStrip } from "../ui/NoticeStrip";
import { SectionLabel } from "../ui/SectionLabel";
import { formatMoney } from "../../lib/api-orders";

interface Props {
  /** Есть ли право `SHOP_MANAGE_ORDERS`: без него заказ не создать. */
  canManage: boolean;
  goodsTotal: number;
  /** Стоимость доставки; `null` — доставка не выставляется. */
  deliveryAmount: number | null;
  /** Что писать в строке «Доставка», когда суммы нет. */
  deliveryNote: string;
  customerEmail: string | null;
  /** Почему нельзя отправить письмо; `null` — можно. */
  blockedReason: string | null;
}

export function OrderCreateSubmitCard({
  canManage,
  goodsTotal,
  deliveryAmount,
  deliveryNote,
  customerEmail,
  blockedReason,
}: Props) {
  const total = goodsTotal + (deliveryAmount ?? 0);
  const delivery =
    deliveryAmount === null
      ? deliveryNote
      : deliveryAmount === 0
        ? "0,00 ₽ (бесплатно)"
        : `${formatMoney(String(deliveryAmount))} ₽`;

  return (
    <EditorialCard>
      <EditorialCard.Body className="flex flex-col gap-3">
        <SectionLabel as="h2">Итого и отправка</SectionLabel>
        <dl className="type-body flex flex-col gap-1.5">
          <Line label="Товары" value={`${formatMoney(String(goodsTotal))} ₽`} />
          <Line label="Доставка" value={delivery} />
          <Line label="К оплате" value={`${formatMoney(String(total))} ₽`} highlight />
        </dl>

        {canManage ? null : (
          <NoticeStrip>Создавать заказы может менеджер заказов — у вас только просмотр.</NoticeStrip>
        )}

        <p className="type-body text-on-surface-variant">
          {customerEmail
            ? `Письмо уйдёт на ${customerEmail} · ссылка действует 72 часа`
            : "У клиента нет e-mail: письмо отправить некуда — согласуйте по телефону."}
        </p>
        {blockedReason ? (
          <p className="type-body text-[var(--status-attention-fg,var(--warning))]">{blockedReason}</p>
        ) : null}
      </EditorialCard.Body>
    </EditorialCard>
  );
}

/** Строка итога: подпись слева, сумма справа табличными цифрами. */
function Line({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-on-surface-variant">{label}</dt>
      <dd className={highlight ? "type-card-title tabular-nums text-on-surface" : "tabular-nums text-on-surface"}>
        {value}
      </dd>
    </div>
  );
}
